/**
 * @file pickup-dropoff.service.js
 * @module PickupDropoffService
 * @description Rules for a product's pickup and dropoff location lists, mirroring
 * the backend validation so the form can explain what is wrong before saving:
 * - `PRIVATE_TRANSFER`: no locations, or both lists with locations.
 * - `TOUR`: pickup locations only.
 * - Other product types: no locations.
 * - Each list has at most one default and at most 20 locations. Without a
 *   default, OCTO resellers must choose a location.
 * - Every location lies inside the product area polygon.
 */

import isPointInPolygon from "geolib/es/isPointInPolygon";

export const MAX_PICKUP_DROPOFF_LOCATIONS = 20;

const PICKUP_PRODUCT_TYPES = ["PRIVATE_TRANSFER", "TOUR"];
const DROPOFF_PRODUCT_TYPES = ["PRIVATE_TRANSFER"];
const AIRPORT_TRANSFER_TYPES = ["ARRIVAL", "DEPARTURE"];

const LOCATION_LISTS = [
  { key: "pickupLocations", label: "Pickup Locations" },
  { key: "dropoffLocations", label: "Dropoff Locations" }
];

export const canHavePickupLocations = type => PICKUP_PRODUCT_TYPES.includes(type);

export const canHaveDropoffLocations = type =>
  DROPOFF_PRODUCT_TYPES.includes(type);

/**
 * Search type the location modal opens with: airport transfers start or end
 * at an airport, so they search airports first.
 * @param {string} transferType
 * @returns {"airport"|"address"}
 */
export const getLocationSearchType = transferType =>
  AIRPORT_TRANSFER_TYPES.includes(transferType) ? "airport" : "address";

/**
 * Lists of the product that have locations, with their display label.
 * @param {Object} product
 * @returns {Array<{ key: string, label: string }>}
 */
export const getLocationListsToShow = (product = {}) =>
  LOCATION_LISTS.filter(list => (product[list.key] || []).length);

/**
 * Street address of a location, or an empty string.
 * @param {Object} location
 * @returns {string}
 */
export const getLocationAddress = (location = {}) => {
  const place = location.place || {};
  return (place.postalAddress || {}).streetAddress || "";
};

/**
 * Whether a list has a default location.
 * @param {Object[]} locations
 * @returns {boolean}
 */
export const hasDefaultLocation = (locations = []) =>
  locations.some(location => location.isDefault);

/**
 * Whether a place has what the backend requires: coordinates and country.
 * @param {Object|null} place
 * @returns {boolean}
 */
export const hasValidPlace = place =>
  !!(
    place &&
    place.latitude &&
    place.longitude &&
    place.postalAddress &&
    place.postalAddress.addressCountry
  );

/**
 * Point-in-polygon test with geolib, the same check the backend runs.
 * @param {{ latitude: number, longitude: number }} place
 * @param {Array<{ lat: number, lng: number }>} polygon
 * @returns {boolean}
 */
export const isPlaceInPolygon = (place = {}, polygon = []) => {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;
  return isPointInPolygon(
    { lat: place.latitude, lng: place.longitude },
    polygon
  );
};

/**
 * Whether a location with a place lies outside the area polygon. Returns
 * false while the polygon is not loaded (the backend has the last word).
 * @param {Object} location
 * @param {Array<{ lat: number, lng: number }>|null} polygon
 * @returns {boolean}
 */
export const isLocationOutsideArea = (location = {}, polygon = null) =>
  !!polygon && !!location.place && !isPlaceInPolygon(location.place, polygon);

/**
 * Returns a copy of the list with the location added at the end.
 * @param {Object[]} locations
 * @param {Object} location
 * @returns {Object[]}
 */
export const addLocation = (locations = [], location) => [
  ...locations,
  location
];

/**
 * Returns a copy of the list with the location at `index` replaced.
 * @param {Object[]} locations
 * @param {number} index
 * @param {Object} location
 * @returns {Object[]}
 */
export const replaceLocationAt = (locations = [], index, location) =>
  locations.map((current, position) =>
    position === index ? location : current
  );

/**
 * Returns a copy of the list without the location at `index`.
 * @param {Object[]} locations
 * @param {number} index
 * @returns {Object[]}
 */
export const removeLocationAt = (locations = [], index) =>
  locations.filter((location, position) => position !== index);

/**
 * Toggles the default of a list: marks the given location as the only default,
 * or leaves the list without default when it already was. Without a default,
 * OCTO resellers must choose a location when booking.
 * @param {Object[]} locations
 * @param {number} index
 * @returns {Object[]}
 */
export const toggleDefaultLocation = (locations = [], index) => {
  const isDefault = !!(locations[index] && locations[index].isDefault);
  return locations.map((location, position) => ({
    ...location,
    isDefault: !isDefault && position === index
  }));
};

/**
 * Copies a list for a new product: locations without id so the backend
 * assigns new ones.
 * @param {Object[]} locations
 * @returns {Object[]}
 */
export const copyLocationsWithoutIds = (locations = []) =>
  locations.map(location => {
    const copy = JSON.parse(JSON.stringify(location));
    delete copy.id;
    return copy;
  });

const getPickups = product => product.pickupLocations || [];
const getDropoffs = product => product.dropoffLocations || [];
const countDefaults = locations =>
  locations.filter(location => location.isDefault).length;

/**
 * The product has pickup locations but its type does not allow them.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasPickupsNotAllowed = (product = {}) =>
  !!getPickups(product).length && !canHavePickupLocations(product.type);

/**
 * The product has dropoff locations but its type does not allow them.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasDropoffsNotAllowed = (product = {}) =>
  !!getDropoffs(product).length && !canHaveDropoffLocations(product.type);

/**
 * A private transfer with only one of the two lists.
 * @param {Object} product
 * @returns {boolean}
 */
export const isIncompletePrivateTransfer = (product = {}) =>
  product.type === "PRIVATE_TRANSFER" &&
  !getPickups(product).length !== !getDropoffs(product).length;

/**
 * The pickup list is over the maximum size.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasTooManyPickups = (product = {}) =>
  getPickups(product).length > MAX_PICKUP_DROPOFF_LOCATIONS;

/**
 * The dropoff list is over the maximum size.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasTooManyDropoffs = (product = {}) =>
  getDropoffs(product).length > MAX_PICKUP_DROPOFF_LOCATIONS;

/**
 * The pickup list has more than one default location.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasMultiplePickupDefaults = (product = {}) =>
  countDefaults(getPickups(product)) > 1;

/**
 * The dropoff list has more than one default location.
 * @param {Object} product
 * @returns {boolean}
 */
export const hasMultipleDropoffDefaults = (product = {}) =>
  countDefaults(getDropoffs(product)) > 1;

/**
 * Some location lies outside the area polygon. False while the polygon is not
 * loaded (the backend has the last word).
 * @param {Object} product
 * @param {Array<{ lat: number, lng: number }>|null} polygon
 * @returns {boolean}
 */
export const hasLocationsOutsideArea = (product = {}, polygon = null) =>
  [...getPickups(product), ...getDropoffs(product)].some(location =>
    isLocationOutsideArea(location, polygon)
  );
